import { useState } from "react";
import { useT } from "../i18n/hooks.ts";

/**
 * Persistent, hard-to-miss notice that the last write to IndexedDB actually
 * FAILED — deliberately not tucked inside the notices bell
 * (NoticesPopover.tsx), which requires an extra click and the user might not
 * check right before refreshing/closing the tab, exactly when it matters
 * most. Top-center (not `.ephemeral-banner`'s bottom-center spot) so it
 * stays visible together with that one, and with the drawing/MITECO banners,
 * without any of them needing to know about each other.
 *
 * See `saveError`'s own comment in appState.ts for the real incident this
 * was built for (a Duero-scale — 1728 sub-basins — project silently losing
 * an "Ampliar" batch and a "Guardar copia como" to an out-of-memory write,
 * with nothing but a `console.error` to show for it).
 */
export function SaveErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT();
  const [retrying, setRetrying] = useState(false);

  return (
    <div className="save-error-banner">
      <span aria-hidden="true">🔴</span>
      <span>{message}</span>
      <button
        className="button button-primary"
        disabled={retrying}
        onClick={() => {
          setRetrying(true);
          // No promise to await here — `onRetry` (retrySave) reports its own
          // outcome by changing/clearing `saveError` itself; this button
          // only needs to stop looking clickable for a moment so a slow
          // retry (the whole point of it failing before) isn't mistaken for
          // doing nothing. Re-enabled shortly after regardless of outcome —
          // a new `saveError` message (still set, or freshly set again)
          // re-renders this same banner either way.
          onRetry();
          window.setTimeout(() => setRetrying(false), 1500);
        }}
      >
        {retrying ? t("saveErrorBanner.retrying") : t("saveErrorBanner.retry")}
      </button>
    </div>
  );
}
